// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx

import { CaretDown } from "@phosphor-icons/react";
import { Text, Tooltip } from "@radix-ui/themes";
import { Link as RouterLink } from "@tanstack/react-router";
import { type ReactNode, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useWordClamp } from "./useWordClamp";

import { citationCardClass } from "../../components/ask-puntt-sources";
import { MemoryChipStatic } from "../../components/memory-citation/MemoryChipFace";
import {
  clipAtWord,
  memoryMeta,
} from "../../components/memory-citation/memoryLabels";
import {
  MEMORY_LINK_TO,
  memoryTabSearch,
} from "../../components/memory-citation/memoryLink";
import { type MemoryCitation } from "../../utils/memoryCitationFields";
import {
  memoryFocusKey,
  useMemoryFocus,
  useMemoryLitHandlers,
} from "../../utils/memoryCitationFocus";

/** What a screen reader reads of a collapsed card whose clamp cannot be measured. */
const DESCRIPTION_CHARS = 120;
/** The card text's line height; the clamp counts lines in these. Keep it equal to `leading-[19px]` below. */
const TEXT_LINE_HEIGHT_PX = 19;
const TEXT_LINES = 2;
const TEXT_CLASS =
  "whitespace-pre-line text-[13px] leading-[19px] text-puntt-neutral-gray-12";

/**
 * One saved memory the answer uses, in the rail.
 *
 * The same visual language as the Cited claims cards: a numbered chip, the text,
 * a quiet meta line. The chip is the one in the answer (`M1`).
 *
 * Hovering or focusing the card tints the sentence the memory supports in the
 * answer, the way hovering the chip does. Click opens the whole text.
 *
 * why no copy of that sentence on the card, and no button to scroll to it: the
 * tint already shows which sentence the memory rests on, and a quote of it under
 * every card only repeated the answer.
 *
 * why the badge is its own row: beside the sentence, the chip spent the width
 * the sentence needs, and a wider chip ("M10") shoved the text further in.
 * The badge sits alone; the sentence takes the card's full width under it.
 * The caret stays on the badge row so it does not eat that width.
 *
 * why the type, date and origin stay inside the toggle: they are detail about
 * the note. The collapsed card is the badge and the sentence; opening it
 * reveals the meta, the saved-note line and the link into Memories.
 *
 * why the clamp is measured and not CSS alone: `line-clamp-2` ends the last line
 * where the line ends, and Chrome does that mid-word ("Magnesiu..."). The text
 * shown is the longest run of whole words that fits two lines (`useWordClamp`);
 * the CSS clamp stays as the net where nothing can be measured.
 *
 * A memory whose words are only warhol's 120-character edge label (`clipped`) is
 * shown as cut, with no toggle that would reveal nothing more, and links to
 * Memories straight away: the whole memory is there.
 */
export function AskV2RailMemoryCard({
  memory,
  number,
  scope,
}: {
  memory: MemoryCitation;
  number: number;
  /** The message the memory is cited in: with the memory id, the link to its chip. */
  scope: string;
}) {
  const { t, i18n } = useTranslation("ui");
  const [expanded, setExpanded] = useState(false);
  const key = memoryFocusKey(scope, memory.id);
  const { lit } = useMemoryFocus(key);
  const litHandlers = useMemoryLitHandlers(key);
  const meta = memoryMeta(memory, t, i18n.language);
  const id = memory.id;
  const prefix = `ask-v2-rail-memory-card-${id}`;
  const textId = useId();
  const descriptionId = useId();
  const dated = [meta.type, meta.date].filter(Boolean);
  const whole = memory.clipped !== true;
  // A card with nothing more to reveal shows its note and its link at once.
  const open = expanded || !whole;
  const { probeRef, clipped } = useWordClamp(
    memory.text,
    TEXT_LINES,
    TEXT_LINE_HEIGHT_PX,
    whole && !expanded,
  );
  // why not the visible text while collapsed: where nothing can be measured it holds the whole memory under a CSS clamp, and a description is read in full, clamp or not.
  const collapsedDescription =
    clipped ?? clipAtWord(memory.text, DESCRIPTION_CHARS);
  // why a short name and a description: the toggle's own text is the whole memory, which a screen reader would read as the button's name.
  const toggleName = meta.type
    ? t("askPuntt.memoryCitation.chipAriaWithType", {
        n: number,
        type: meta.type,
      })
    : t("askPuntt.memoryCitation.chipAria", { n: number });
  const header = (caret?: ReactNode) => (
    <>
      <span className="flex w-full items-center">
        <MemoryChipStatic
          number={number}
          testId={`${prefix}-number`}
          className="min-w-11"
        />
        {caret}
      </span>
      <span className="relative block w-full min-w-0">
        <span
          id={textId}
          data-testid={`${prefix}-text`}
          className={`block ${TEXT_CLASS} ${
            whole && !expanded ? "line-clamp-2" : ""
          }`}
        >
          {whole && !expanded && clipped ? clipped : memory.text}
        </span>
        {/* The probe the clamp measures in: same type, no layout, no reader. */}
        <span
          ref={probeRef}
          aria-hidden="true"
          data-testid={`${prefix}-text-probe`}
          className={`pointer-events-none invisible absolute inset-x-0 top-0 ${TEXT_CLASS}`}
        />
      </span>
    </>
  );

  return (
    <li
      {...litHandlers}
      data-testid={prefix}
      data-lit={lit ? "true" : "false"}
      className="puntt-memory-card flex flex-col rounded-lg border border-puntt-neutral-gray-5 bg-base-white"
    >
      {whole ? (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          aria-expanded={expanded}
          aria-label={toggleName}
          aria-describedby={expanded ? textId : descriptionId}
          data-testid={`${prefix}-toggle`}
          // why bg-transparent: preflight is off, so a bare <button> paints the UA grey over the card.
          className="flex w-full flex-col items-stretch gap-1.5 rounded-lg bg-transparent px-3 pb-1 pt-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--puntt-link-memory)]"
        >
          {header(
            <CaretDown
              size={14}
              aria-hidden="true"
              className={`ml-auto shrink-0 text-puntt-neutral-gray-11 transition-transform ${
                expanded ? "" : "-rotate-90"
              }`}
            />,
          )}
          {expanded ? null : (
            <span
              id={descriptionId}
              hidden
              data-testid={`${prefix}-description`}
            >
              {collapsedDescription}
            </span>
          )}
        </button>
      ) : (
        <div className="flex w-full flex-col items-stretch gap-1.5 px-3 pb-1 pt-2.5">
          {header()}
        </div>
      )}
      {open ? (
        <div data-testid={`${prefix}-body`} className="min-w-0 px-3 pb-2.5">
          {dated.length > 0 || meta.origin ? (
            <p
              data-testid={`${prefix}-meta`}
              className="text-[12px] leading-4 text-puntt-neutral-gray-11"
            >
              {dated.length > 0 ? (
                <>
                  {meta.type ? (
                    <span className="font-semibold text-puntt-neutral-gray-11">
                      {meta.type}
                    </span>
                  ) : null}
                  {meta.type && meta.date ? " · " : null}
                  {meta.date}
                </>
              ) : null}
              {meta.origin ? (
                <span className="block" data-testid={`${prefix}-origin`}>
                  {meta.origin}
                </span>
              ) : null}
            </p>
          ) : null}
          <p
            data-testid={`${prefix}-note`}
            className="mt-2 text-[12px] leading-4 text-puntt-neutral-gray-11"
          >
            {t("askPuntt.memoryCitation.savedNote")}
          </p>
          <RouterLink
            to={MEMORY_LINK_TO}
            search={memoryTabSearch(memory.text)}
            data-testid={`${prefix}-open-in-memories`}
            className="mt-2 block w-fit text-[12px] font-semibold leading-4 text-[color:var(--puntt-link-memory)] no-underline hover:underline"
          >
            {t("askPuntt.memoryCitation.openInMemories")}
          </RouterLink>
        </div>
      ) : null}
    </li>
  );
}

function memoryTextOverflows(el: HTMLElement): boolean {
  // why: line-clamp hides the tail vertically; a single-line clip hides it
  // horizontally. Either one means the row is not the whole memory.
  return (
    el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1
  );
}

/**
 * A saved memory in the context list, drawn as the same card as a citation
 * and opened in the Knowledge Database by its own words.
 */
export function AskV2ContextMemoryCard({
  text,
  testId,
  icon,
}: {
  text: string;
  testId: string;
  icon?: ReactNode;
}) {
  const { t } = useTranslation("ui");
  const textRef = useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <RouterLink
      to={MEMORY_LINK_TO}
      search={memoryTabSearch(text)}
      data-testid={testId}
      aria-label={t("askV2.rail.citedMemoryAria", { text })}
      className={`${citationCardClass()} no-underline`}
    >
      <span className="flex min-w-0 items-start gap-2">
        {icon}
        <Tooltip
          content={
            <span className="block max-w-[280px] whitespace-pre-wrap">
              {text}
            </span>
          }
          open={tooltipOpen}
          delayDuration={200}
        >
          <Text
            as="span"
            ref={textRef}
            size="2"
            weight="medium"
            data-testid={`${testId}-text`}
            className="line-clamp-3 min-w-0 text-puntt-neutral-gray-12"
            onMouseEnter={() => {
              const el = textRef.current;
              if (el) setTooltipOpen(memoryTextOverflows(el));
            }}
            onMouseLeave={() => setTooltipOpen(false)}
          >
            {text}
          </Text>
        </Tooltip>
      </span>
    </RouterLink>
  );
}
